import {Switch} from '@mantine/core';
import React from 'react';

import {SchemaTag} from 'folder-items/FileConstants';
import {ALLOWED_TIMEZONE} from 'graphql-client/__generated/GeneratedEnumMaps';
import {ConnectionFieldType} from 'instruments/connections/ConnectionConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import SecureTextInput from 'ui-library/inputs/SecureTextInput';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

function getTimezoneEditor(
  tag: SchemaTag,
  autoFocus: boolean,
  onChange: (newValue: Partial<SchemaTag>) => void
): JSX.Element {
  return (
    <SelectDropdown
      menu={
        <SearchableMenu
          selectedValue={tag.value}
          optionDatas={Object.values(ALLOWED_TIMEZONE).map((tz) => ({
            name: tz,
            value: tz,
          }))}
          onSelect={(timezone: string | null) => {
            onChange({value: timezone});
          }}
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.LARGE}
          scrollable={true}
        />
      }
      displayName={tag.value}
      autoFocus={autoFocus}
    />
  );
}

function getSecureEditor(
  tag: SchemaTag,
  autoFocus: boolean,
  onChange: (newValue: Partial<SchemaTag>) => void
): JSX.Element {
  return (
    <SecureTextInput
      name={tag.name}
      value={tag.value || null}
      onChange={(newValue: string) => {
        onChange({value: newValue});
      }}
      disabled={false}
      description={`After saving ${tag.name.toLowerCase()} this will no longer be visible.`}
    />
  );
}

function getPerUserLoginRequiredEditor(
  tag: SchemaTag,
  autoFocus: boolean,
  onChange: (newValue: Partial<SchemaTag>) => void
): JSX.Element {
  return (
    <Switch
      checked={tag.value === 'true'}
      onChange={(event) => {
        onChange({value: String(event.currentTarget.checked)});
      }}
    />
  );
}

function getEnableAutoSyncEditor(
  hasCredentials: boolean,
  tag: SchemaTag,
  autoFocus: boolean,
  onChange: (newValue: Partial<SchemaTag>) => void
): JSX.Element {
  return (
    <Tooltip text="Username and password must be set to enable auto-sync" isDisabled={hasCredentials}>
      <Switch
        checked={tag.value === 'true'}
        disabled={!hasCredentials}
        onChange={(event) => {
          onChange({value: String(event.currentTarget.checked)});
        }}
      />
    </Tooltip>
  );
}

export default function getCustomTagEditors(
  fieldTypes: {[id: string]: string | null},
  hasCredentials: boolean = false
): {
  [id: string]: (
    tag: SchemaTag,
    autoFocus: boolean,
    onChange: (newValue: Partial<SchemaTag>) => void
  ) => JSX.Element;
} {
  const customEditors: {
    [id: string]: (
      tag: SchemaTag,
      autoFocus: boolean,
      onChange: (newValue: Partial<SchemaTag>) => void
    ) => JSX.Element;
  } = {
    default_timezone: getTimezoneEditor,
    per_user_login_required: getPerUserLoginRequiredEditor,
    enable_auto_sync: (tag, autoFocus, onChange) =>
      getEnableAutoSyncEditor(hasCredentials, tag, autoFocus, onChange),
  };
  Object.entries(fieldTypes).forEach(([fieldName, fieldType]) => {
    if (fieldType === ConnectionFieldType.SECRET_REF) {
      customEditors[fieldName] = getSecureEditor;
    }
  });
  return customEditors;
}

const readOnlyRootStyle = {pointerEvents: 'none' as const};

function readOnlySwitchViewer(tag: SchemaTag): JSX.Element {
  return (
    <Tooltip text={tag.value === 'true' ? 'Enabled (true)' : 'Disabled (false)'}>
      <Switch checked={tag.value === 'true'} readOnly={true} styles={{root: readOnlyRootStyle}} />
    </Tooltip>
  );
}

export function getCustomTagViewers(): {[id: string]: (tag: SchemaTag) => JSX.Element} {
  return {
    per_user_login_required: readOnlySwitchViewer,
    enable_auto_sync: readOnlySwitchViewer,
  };
}
